import React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';

const OrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status') || 'success';
  const orderId = searchParams.get('order_id') || '';

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center px-4 pt-24 pb-12" data-testid="order-success-page">
        <SEO title="Order Confirmed" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white rounded-2xl border border-[#E8DFD3] shadow-lg p-8 text-center"
        >
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200 }}>
            <CheckCircle2 size={72} className="mx-auto text-[#5C7A3A] mb-4" />
          </motion.div>
          <h2 className="text-3xl font-bold text-[#2C1810] mb-2" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="order-success">
            Order Confirmed!
          </h2>
          <p className="text-base text-[#7a6555] mb-2">
            Thank you for your order. Payment was successful.
          </p>
          {orderId && (
            <p className="text-sm text-[#7a6555] mb-6">
              Order ID: <span className="font-mono font-semibold text-[#2C1810]">#{orderId.slice(0, 8)}</span>
            </p>
          )}
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/products">
              <Button className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer" data-testid="continue-shopping-btn">
                Continue Shopping
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-[#E8DFD3] text-[#2C1810] hover:bg-[#FDF8F0] cursor-pointer" data-testid="go-home-btn">
                Back to Home
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center px-4 pt-24 pb-12" data-testid="order-success-page">
        <SEO title="Payment Failed" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white rounded-2xl border border-[#E8DFD3] shadow-lg p-8 text-center"
        >
          <XCircle size={56} className="mx-auto text-red-400 mb-4" />
          <h2 className="text-2xl font-bold text-[#2C1810] mb-2" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="order-failed">
            Payment Failed
          </h2>
          <p className="text-sm text-[#7a6555] mb-6">
            Your payment could not be verified. Please try again or contact support.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/checkout">
              <Button className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer">Try Again</Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-[#E8DFD3] text-[#2C1810] hover:bg-[#FDF8F0] cursor-pointer">Contact Support</Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  // Error / unknown state
  return (
    <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center px-4 pt-24 pb-12" data-testid="order-success-page">
      <SEO title="Order Status" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-2xl border border-[#E8DFD3] shadow-lg p-8 text-center"
      >
        <AlertCircle size={56} className="mx-auto text-[#C4844A] mb-4" />
        <h2 className="text-2xl font-bold text-[#2C1810] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          Something Went Wrong
        </h2>
        <p className="text-sm text-[#7a6555] mb-6">
          We couldn't verify your payment status. Please check your email for confirmation or contact support.
        </p>
        <Link to="/contact">
          <Button className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer">Contact Support</Button>
        </Link>
      </motion.div>
    </div>
  );
};

export default OrderSuccess;
